import { Button, FacebookIcon, GoogleMonoIcon, TextInput } from "@fla/components";
import { yupResolver } from "@hookform/resolvers/yup";
import { useAppDispatch } from "app/store";
import { LoginSchema } from "definitions/Yup";
import { useRequesting } from "hooks";
import { AuthType, SliceStatus, ThirdPartyAuthProviders, UserRoles } from "interfaces";
import { FC } from "react";
import { Helmet } from "react-helmet-async";
import { useForm } from "react-hook-form";
import { Link } from "react-router-dom";

import { userActions } from "../userSlice";

const Login: FC<{ as?: "page" | "modal"; href: string }> = ({ as = "page", href = "" }) => {
  const dispatch = useAppDispatch();
  const isSubmitting = useRequesting("user") === SliceStatus.Pending;

  const { handleSubmit, control, setError } = useForm({
    mode: "onSubmit",
    defaultValues: { email: "", password: "" },
    resolver: yupResolver(LoginSchema)
  });

  const onSubmit = (payload: { email: string; password: string }) => {
    dispatch(userActions.login({ ...payload, setError, href }));
  };

  const loginUsingThirdParty = (providerType: ThirdPartyAuthProviders) => {
    dispatch(
      userActions.thirdPartyAuth({
        role: UserRoles.user,
        authType: AuthType.LOGIN,
        providerType,
        href
      })
    );
  };

  return (
    <>
      <Helmet>
        <title>SplitWise | Login</title>
      </Helmet>
      <section className="w-full md:max-w-md space-y-7">
        <h2 className="text-secondary outfit-medium">Login To Your Account</h2>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
          <TextInput
            type="email"
            title="Email Address"
            control={control}
            name="email"
            id="email"
            placeholder="Email Address"
          />
          <TextInput
            title="Password"
            control={control}
            name="password"
            id="password"
            placeholder="Password"
            type="password"
          />

          <Button
            className="w-full h-8 bg-primary font-medium flex justify-center items-center space-x-3 tracking-wider"
            fontSize="xs"
            borderRadius="lg"
            type="submit"
            isSubmitting={isSubmitting}
          >
            LOG IN
          </Button>
        </form>

        <div className="space-y-3">
          <div className="pb-3">
            <p className="text-xs">
              By creating an account you agree to our{" "}
              <span className="text-primary hover:underline underline-offset-4 cursor-pointer">Terms of Service</span>{" "}
              and <span className="text-primary hover:underline underline-offset-4 cursor-pointer">Privacy Policy</span>
            </p>
          </div>
          <div className="space-y-3">
            <p className="text-sm font-bold">Or Login In Using</p>
            <div className="flex space-x-3 items-center">
              <Button
                className="bg-primary/[15%] hover:bg-primary py-1.5 w-28 rounded-lg group"
                disabled={isSubmitting}
                onClick={() => loginUsingThirdParty(ThirdPartyAuthProviders.GOOGLE)}
              >
                <GoogleMonoIcon classes="w-4 h-auto" strokeColor="text-primary group-hover:text-white" />
                <span className="text-primary group-hover:text-white font-semibold text-sm">Google</span>
              </Button>
              <Button
                className="bg-primary/[15%] hover:bg-primary py-1.5 w-28 rounded-lg group hidden"
                disabled={isSubmitting}
                onClick={() => loginUsingThirdParty(ThirdPartyAuthProviders.FACEBOOK)}
              >
                <FacebookIcon classes="w-4 h-auto" strokeColor="text-primary group-hover:text-white" />
                <span className="text-primary group-hover:text-white font-semibold text-sm">Facebook</span>
              </Button>
            </div>
          </div>

          {as === "page" ? (
            <div className="pt-3">
              <Link to="/register" className="text-sm">
                Don't have an Account?&nbsp;
                <span className="font-semibold text-primary hover:underline underline-offset-4">
                  Create An Account Here
                </span>
              </Link>
            </div>
          ) : null}
        </div>
      </section>
    </>
  );
};
export default Login;
